import React, { useState, useEffect } from 'react';
import { FileSpreadsheet, Download, Loader2 } from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';
import { useDatabase } from '../../context/DatabaseContext';
import { useDataTransfer } from '../../context/DataTransferContext';
import api from '../../services/api';
import { todayKey } from '../../utils/dateTime';

const PowerQueryPage = () => {
  const { notify } = useNotifications();
  const { dbSlug } = useDatabase();
  const { trackTransfer } = useDataTransfer();
  const [generating, setGenerating] = useState(false);
  // Tab count comes from the API so this page can't drift out of sync with
  // ViewTabMapping again — it hardcoded "9 views" while the mapping held 8.
  const [viewCount, setViewCount] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get('/api/projectexport/views')
      .then(r => { if (!cancelled) setViewCount(r.data?.count ?? null); })
      .catch(() => { /* label just falls back to generic text */ });
    return () => { cancelled = true; };
  }, []);

  const handleGenerate = async () => {
    setGenerating(true);

    try {
      await trackTransfer({ name: `${dbSlug || 'export'} Power Query`, format: 'excel' }, async () => {
        const response = await api.get('/api/projectexport/generate', {
          responseType: 'blob'
        });

        // Extract filename from Content-Disposition header or use default
        const contentDisposition = response.headers['content-disposition'];
        // Fallback only — the server sets the real name via Content-Disposition. Keep the
        // shape matching the server's ({slug}_Power_Query_...) so the two never disagree.
        let fileName = `${dbSlug || 'export'}_Power_Query_${todayKey().replace(/-/g, '')}.xlsx`;

        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?([^";\n]+)"?/i);
          if (match) {
            fileName = match[1];
          }
        }

        // Create download link
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      });

      notify.success('Power Query export generated successfully');
    } catch (err) {
      console.error('Export failed:', err);
      notify.error('Failed to generate export: ' + (err.response?.data?.error || err.message));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="stui-module-container">
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <FileSpreadsheet size={20} />
          <h1>Power Query</h1>
          <span className="stui-module-header-meta">{viewCount != null ? `${viewCount} views` : 'views'}</span>
        </div>
      </div>

      <div className="stui-tab-body">
        <div className="stui-step-panel">
          <div className="stui-step-header">
            <span className="stui-step-number">1</span>
            <h3>Generate Export</h3>
          </div>
          <p className="stui-step-desc">
            Generate Excel workbook with project data from
            {viewCount != null ? ` ${viewCount} ` : ' '}database views.
            Each view becomes a separate tab in the workbook.
          </p>

          <button
            className="stui-btn stui-btn-primary"
            onClick={handleGenerate}
            disabled={generating}
          >
            {generating ? (
              <>
                <Loader2 size={16} className="stui-spinning" />
                Generating...
              </>
            ) : (
              <>
                <Download size={16} />
                Generate Export
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PowerQueryPage;
